(() => {
  if (window.SakuraWiseReviewsFallback) {
    window.SakuraWiseReviewsFallback.initialize();
    return;
  }

  const API_BASE = 'https://wise-reviews.codexapps.co/api/proxy/reviews';
  const WAIT_MS = 6500;
  const FETCH_TIMEOUT_MS = 8000;
  const NATIVE_POLL_MS = 500;
  const controllers = new WeakMap();

  const makeElement = (tagName, className, text) => {
    const element = document.createElement(tagName);
    if (className) element.className = className;
    if (text) element.textContent = text;
    return element;
  };

  const nativeFeedHasContent = (container) => {
    const featured = container.querySelector('wise-featured-reviews');
    if (!featured) return false;
    return Boolean(featured.querySelector('.wise-review-content-container, .featured-reviews [data-product-card-tracked="true"]'));
  };

  const fallbackOutput = (container) => container.querySelector('[data-sakura-review-fallback-output]');

  const setNativeFallbackVisibility = (container, hidden) => {
    const featured = container.querySelector('wise-featured-reviews');
    if (!featured) return;
    if (hidden) {
      featured.setAttribute('aria-hidden', 'true');
      featured.setAttribute('inert', '');
    } else {
      featured.removeAttribute('aria-hidden');
      featured.removeAttribute('inert');
    }
  };

  const removeFallback = (container) => {
    const fallback = fallbackOutput(container);
    if (fallback) fallback.remove();
    setNativeFallbackVisibility(container, false);
    container.dataset.sakuraFallbackState = 'native';
  };

  const replaceFallbackOutput = (container, output, state) => {
    const existing = fallbackOutput(container);
    if (existing) existing.remove();
    output.setAttribute('data-sakura-review-fallback-output', '');
    setNativeFallbackVisibility(container, true);
    container.append(output);
    container.dataset.sakuraFallbackState = state;
  };

  const renderStatus = (container, message) => {
    replaceFallbackOutput(container, makeElement('p', 'sakura-home-reviews__status', message), 'unavailable');
  };

  const renderReviews = (container, reviews) => {
    const list = makeElement('div', 'sakura-home-reviews__fallback');
    list.setAttribute('role', 'list');

    reviews.slice(0, 3).forEach((review) => {
      const card = makeElement('article', 'sakura-review-card');
      card.setAttribute('role', 'listitem');

      const rating = Math.max(0, Math.min(5, Number(review.rating) || 0));
      const stars = makeElement('div', 'sakura-review-card__stars', '★'.repeat(rating));
      stars.setAttribute('aria-label', `${rating} out of 5 stars`);
      card.append(stars);

      if (review.title) card.append(makeElement('h3', '', review.title));
      if (review.body) card.append(makeElement('p', 'sakura-review-card__body', review.body));

      const byline = makeElement('p', 'sakura-review-card__byline');
      if (review.reviewer_name) byline.append(makeElement('strong', '', review.reviewer_name));
      if (review.verified) byline.append(makeElement('span', '', 'Verified'));
      if (review.date) byline.append(makeElement('time', '', review.date));
      card.append(byline);
      list.append(card);
    });

    replaceFallbackOutput(container, list, 'loaded');
  };

  const settleNative = (section, container) => {
    removeFallback(container);
    const controller = controllers.get(section);
    if (!controller) return;
    window.clearTimeout(controller.timer);
    if (controller.poll) window.clearInterval(controller.poll);
    controller.observer.disconnect();
    if (controller.request) controller.request.abort();
  };

  const activateFallback = (section) => {
    if (controllers.has(section)) return;

    const container = section.querySelector('.sakura-home-reviews__app');
    if (!container) return;
    const latestStorewide = section.dataset.reviewMode === 'latest-storewide';

    const controller = {
      observer: null,
      poll: null,
      request: null,
      timer: null
    };
    controllers.set(section, controller);

    controller.observer = new MutationObserver(() => {
      if (!latestStorewide && nativeFeedHasContent(container)) settleNative(section, container);
    });
    if (!latestStorewide) {
      controller.observer.observe(container, { childList: true, subtree: true, characterData: true });
      controller.poll = window.setInterval(() => {
        if (nativeFeedHasContent(container)) settleNative(section, container);
      }, NATIVE_POLL_MS);
    }

    controller.timer = window.setTimeout(async () => {
      if (!latestStorewide && nativeFeedHasContent(container)) {
        settleNative(section, container);
        return;
      }

      const shop = section.dataset.reviewsShop;
      if (!shop) {
        renderStatus(container, 'Reviews are temporarily unavailable.');
        return;
      }

      const endpoint = new URL(API_BASE);
      endpoint.searchParams.set('itemsPerPage', '3');
      endpoint.searchParams.set('shop', shop);
      endpoint.searchParams.set('sort_by', 'DATE_NEWEST_FIRST');
      endpoint.searchParams.set('preview', 'false');
      endpoint.searchParams.set('cache_version', String(Math.floor(Date.now() / 3600000)));

      controller.request = new AbortController();
      const timeout = window.setTimeout(() => controller.request.abort(), FETCH_TIMEOUT_MS);

      try {
        const response = await fetch(endpoint, {
          headers: { Accept: 'application/json' },
          signal: controller.request.signal
        });
        if (!response.ok) throw new Error(`WiseReviews request failed: ${response.status}`);
        const payload = await response.json();
        const reviews = Array.isArray(payload.data) ? payload.data : [];

        if (!latestStorewide && nativeFeedHasContent(container)) {
          settleNative(section, container);
        } else if (reviews.length > 0) {
          renderReviews(container, reviews);
        } else {
          renderStatus(container, 'No reviews are available yet.');
        }
      } catch (_error) {
        if (!latestStorewide && nativeFeedHasContent(container)) {
          settleNative(section, container);
        } else if (!fallbackOutput(container)) {
          renderStatus(container, 'Reviews are temporarily unavailable.');
        }
      } finally {
        window.clearTimeout(timeout);
      }
    }, latestStorewide ? 0 : WAIT_MS);

  };

  const initialize = (root = document) => {
    if (root.matches && root.matches('[data-sakura-review-fallback]')) activateFallback(root);
    root.querySelectorAll('[data-sakura-review-fallback]').forEach(activateFallback);
  };

  const destroy = (root) => {
    const sections = [];
    if (root.matches && root.matches('[data-sakura-review-fallback]')) sections.push(root);
    root.querySelectorAll('[data-sakura-review-fallback]').forEach((section) => sections.push(section));
    sections.forEach((section) => {
      const controller = controllers.get(section);
      if (!controller) return;
      window.clearTimeout(controller.timer);
      if (controller.poll) window.clearInterval(controller.poll);
      controller.observer.disconnect();
      if (controller.request) controller.request.abort();
      controllers.delete(section);
    });
  };

  window.SakuraWiseReviewsFallback = { initialize, destroy, nativeFeedHasContent };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();
